import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import {
  FormEditor,
  formToValues,
  validateForm,
  type FormEditorValues,
} from '~/components/FormEditor/FormEditor';
import {
  useTenantForm,
  useUpdateForm,
  useDeleteForm,
} from '~/lib/forms-queries';
import { useTenantContext } from '~/lib/tenantContext';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { formatPbError } from '~/lib/pb-error';
import styles from './_authed.automations.templates_.editor.module.css';

export const Route = createFileRoute('/_authed/automations/forms_/$formId')({
  component: EditFormPage,
});

function EditFormPage() {
  const { formId } = Route.useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { tenants } = useTenantContext();
  const { data: form, isLoading } = useTenantForm(formId);
  const update = useUpdateForm();
  const del = useDeleteForm();

  const [values, setValues] = useState<FormEditorValues | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Hidratar values cuando el form se carga. Mover a useEffect evita el bug
  // React 19 #130 de setState durante render (memoria feedback_react19_set_state_render).
  useEffect(() => {
    if (form && values === null) {
      setValues(formToValues(form));
    }
  }, [form, values]);

  async function handleSave() {
    if (!values) return;
    setError(null);
    const validationError = validateForm(values);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await update.mutateAsync({
        id: formId,
        patch: {
          name: values.name,
          fields_schema: values.fields_schema,
          default_flow: values.default_flow,
          thank_you: values.thank_you,
          active: values.active,
        },
      });
    } catch (err) {
      setError(formatPbError(err, 'Error al guardar'));
    }
  }

  async function handleDelete() {
    if (!form) return;
    const ok = await confirm({
      title: `Eliminar formulario "${form.name}"`,
      description: (
        <>
          El endpoint <code>/api/forms/submit/{form.slug}</code> dejará de
          responder. Esta acción no se puede deshacer.
        </>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(formId);
      void navigate({ to: '/automations/forms' });
    } catch (err) {
      setError(formatPbError(err, 'Error al eliminar'));
    }
  }

  if (isLoading || !values || !form) {
    return (
      <div className={styles.page}>
        <p className="muted">Cargando formulario…</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/automations/forms" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Formularios
        </Link>
        <div className={styles.titleRow}>
          <h2 className="h4" style={{ margin: 0 }}>{form.name || 'Editar formulario'}</h2>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={styles.dangerBtn}
              onClick={handleDelete}
              disabled={del.isPending}
              title="Eliminar"
            >
              <Trash2 size={14} aria-hidden />
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={handleSave}
              disabled={update.isPending}
            >
              <Save size={14} aria-hidden />
              {update.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
        {error && <p className={styles.errorBanner}>{error}</p>}
      </header>

      <FormEditor
        mode="edit"
        values={values}
        onChange={setValues}
        tenants={tenants}
        formId={formId}
        saving={update.isPending}
      />
    </div>
  );
}
